import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { useQueryWithSimpleErrorHandling } from 'hooks/apolloWrappers';
import { GET_PARTNER_HIERARCHY_DETAILS } from "../queries";
import { SAVE_PARTNER_HIERARCHY_DETAIL, REMOVE_PARTNER_HIERARCHY_DETAIL } from "../mutations";
import { useToast, Card, Button } from "@totalsoft/rocket-ui";
import HierarchyDetailsTreeItemComponent from "./HierarchyDetailsTreeItemComponent";
import PartnerAssociationModalEditContainer from "./partnerAssociationModal/PartnerAssociationModalEditContainer";
import AccountTree from "@mui/icons-material/AccountTree";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { get, sequence, useChangeTrackingLens } from "@totalsoft/change-tracking-react";
import { clone } from "ramda";
import { getNodeAtPath, deleteNodeAtPath, getParentPath, newChildForNode, recalculatePaths } from "../functions";
import { useReactiveVar } from "@apollo/client";
import { partnerAssociationModalStateVar } from "./partnerAssociationModal/variables";
import { omit } from "ramda";
import { useSimpleError } from "hooks/apolloWrappers";

const mapTree = (saving, item, handleSave, handleDelete, canWrite, hierarchyName) => {
  if (!item.children || item.children.length === 0) {
    return (
      <HierarchyDetailsTreeItemComponent
        key={item.data.path |> get}
        saving={saving}
        itemId={item.data.path |> get}
        label={item.data.category |> get}
        item={item.data}
        handleSave={handleSave}
        handleDelete={handleDelete}
        canWrite={canWrite}
        hierarchyName={hierarchyName}
      />
    );
  } else {
    return (
      <HierarchyDetailsTreeItemComponent
        key={item.data.path |> get}
        saving={saving}
        itemId={item.data.path |> get}
        label={item.data.category |> get}
        item={item}
        handleSave={handleSave}
        handleDelete={handleDelete}
        canWrite={canWrite}
        hierarchyName={hierarchyName}
      >
        {sequence(item.children).map(child => {
          return mapTree(saving, child, handleSave, handleDelete, canWrite, hierarchyName);
        })}
      </HierarchyDetailsTreeItemComponent>
    );
  }
};

const HierarchyDetailsTreeContainer = ({ partnerHierarchyId, hierarchyName, canWrite }) => {
  const [t] = useTranslation();
  const showToast = useToast();
  const [expandedItems, setExpandedItems] = useState([]);
  const [lens, , resetLens] = useChangeTrackingLens([]);
  const [saving, setSaving] = useState(false);
  const partnerAssociationModalState = useReactiveVar(partnerAssociationModalStateVar);
  const showError = useSimpleError();

  useQueryWithSimpleErrorHandling(GET_PARTNER_HIERARCHY_DETAILS, {
    variables: { partnerHierarchyId },
    skip: !partnerHierarchyId,
    onCompleted: data => {
      resetLens(data?.getPartnerHierarchy?.detailsTree || []);
    }
  });

  const [savePartnerHierarchyDetail] = useMutation(SAVE_PARTNER_HIERARCHY_DETAIL, {
    onError: error => {
          showError(error);
          setSaving(false);
    },
    update: (cache, { data }) => {
      showToast(t("PartnerHierarchy.PartnerHierarchyDetailSaved"), "success", { autoClose: 3000 });
      setSaving(false);
      cache.modify({
        id: `PartnerHierarchy:{"partnerHierarchyId":${data.savePartnerHierarchyDetail.detail.partnerHierarchyId}}`,
        fields: {
          detailsTree: previous => {
            const isNew = data.savePartnerHierarchyDetail.isNew;
            const newDetails = clone(previous);
            let detailToUpdate = getNodeAtPath(newDetails, data.savePartnerHierarchyDetail.detail.path);
            if (!detailToUpdate) {
              detailToUpdate = getNodeAtPath(newDetails, data.savePartnerHierarchyDetail.detail.path.split("/").slice(0, -2).join("/") + "/");
            }
            if (isNew) {
              const newNode = { data: { ...data.savePartnerHierarchyDetail.detail }, children: [] };
              if (detailToUpdate) detailToUpdate.children = [...detailToUpdate.children, newNode];
              else newDetails.push(newNode);
            } else detailToUpdate.data = { ...data.savePartnerHierarchyDetail.detail };
            return newDetails;
          }
        }
      });
      const pathToExpand = getParentPath(data.savePartnerHierarchyDetail.detail.path);
      if (!expandedItems.includes(pathToExpand)) {
        setExpandedItems(prev => [...prev, pathToExpand]);
      }
    }
  });

  const [removePartnerHierarchyDetail] = useMutation(REMOVE_PARTNER_HIERARCHY_DETAIL, {
    onError: error => {
      showError(error);
      setSaving(false);
    },
    update: (cache, { data }) => {
      showToast(t("PartnerHierarchy.PartnerHierarchyDetailRemoved"), "success", { autoClose: 3000 });
      setSaving(false);
      cache.modify({
        id: `PartnerHierarchy:{"partnerHierarchyId":${data.removePartnerHierarchyDetail.partnerHierarchyId}}`,
        fields: {
          detailsTree: previous => {
            const newDetails = clone(previous);

            // Delete the node from the tree
            deleteNodeAtPath(newDetails, data.removePartnerHierarchyDetail.path);
            
            // Get the parent node's path
            const parentPath = getParentPath(data.removePartnerHierarchyDetail.path);
            const parentNode = getNodeAtPath(newDetails, parentPath);
            
            // Recalculate paths to ensure no gaps
            recalculatePaths(newDetails, parentNode);
            
            return newDetails;
          }
        }
      });
    }
  });

  const handleDelete = useCallback(
    (partnerHierarchyDetailId, partnerHierarchyId, path) => {
      setSaving(true);
      removePartnerHierarchyDetail({ variables: { partnerHierarchyDetailId, partnerHierarchyId, path } });
    },
    [removePartnerHierarchyDetail]
  );

  const handleSave = useCallback(
    input => {
      setSaving(true);
      savePartnerHierarchyDetail({ variables: { input: omit(["partnerName"], input) } });
    },
    [savePartnerHierarchyDetail]
  );

  const handleExpand = useCallback(
    (_event, itemId, isExpanded) => {
      setExpandedItems(prev => (isExpanded ? [...prev, itemId] : prev.filter(x => x !== itemId)));
    },
    [setExpandedItems]
  );

  const handleAddNode = useCallback(() => {
    const root = lens |> get;
    handleSave(newChildForNode(getNodeAtPath(root, ""), partnerHierarchyId, root.length));
  }, [handleSave, lens, partnerHierarchyId]);

  return (
    <Card
      icon={AccountTree}
      iconColor="primary"
      title={t("PartnerHierarchy.HierarchyTree")}
      actions={
        <>
          <Button onClick={handleAddNode} disabled={saving}>
            {t("PartnerHierarchy.AddNode")}
          </Button>
        </>
      }
    >
      <DndProvider backend={HTML5Backend}>
        <SimpleTreeView expandedItems={expandedItems} onItemExpansionToggle={handleExpand}>
          {(lens |> sequence)?.map(item => mapTree(saving, item, handleSave, handleDelete, canWrite, hierarchyName))}
        </SimpleTreeView>
      </DndProvider>
      {partnerAssociationModalState?.open && (
        <PartnerAssociationModalEditContainer
          setExpandedItems={setExpandedItems}
          partnerAssociationModalState={partnerAssociationModalState}
        />
      )}
    </Card>
  );
};

HierarchyDetailsTreeContainer.propTypes = {
  partnerHierarchyId: PropTypes.number,
  hierarchyName: PropTypes.string,
  canWrite: PropTypes.bool
};

export default HierarchyDetailsTreeContainer;
